import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Checkbox, Input } from '@material-ui/core';
import {
  find, get, getOr, includes, isArray, last, map, reject, size, some, split, toString,
} from 'lodash/fp';

import { useModal } from '../../../../hooks';
import OtherResponseDialog from '../OtherResponseDialog';
import Question from '../Question';

const MAX_LENGTH = 200;

const S = {
  OptionItem: styled.div`
    position: absolute;
    top: ${({ position }) => `${position.y}%`};
    left: ${({ position }) => `${position.x}%`};
    display: flex;
    align-items: center;
  `,
  Label: styled.label`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
  `,
  OptionNumber: styled.div`
    min-width: ${({ large }) => (large ? 32 : 24)}px;
    padding: 1px 4px 0;
    border-radius: 2px;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background-color: ${({ error, theme }) => (error ? theme.palette.status.error.main : theme.palette.primary.light)};
    font-size: ${({ large, theme }) => theme.typography.pxToRem(large ? 13 : 10)};
    font-weight: 700;
    line-height: ${({ large }) => (large ? 1.54 : 1.5)};
    text-align: center;
    white-space: nowrap;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.32);
  `,
  Mesh: styled.div`
    position: relative;
    display: flex;
    ::before {
      content: '';
      position: absolute;
      top: 50%;
      right: 3px;
      transform: translateY(-50%);
      width: ${({ large }) => (large ? 14 : 10)}px;
      height: ${({ large }) => (large ? 14 : 10)}px;
      opacity: 0.54;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.palette.primary.contrastText};
      ${({ rounded }) => rounded && css`
        right: ${({ large }) => (large ? 3 : 2)}px;
        width: ${({ large }) => (large ? 14 : 12)}px;
        height: ${({ large }) => (large ? 14 : 12)}px;
        border-radius: 50%;
      `}
    }
  `,
  Checkbox: styled(({ large, ...otherProps }) => <Checkbox {...otherProps} />)`
    position: relative;
    padding: 0;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ large }) => (large ? '1.25rem' : '1rem')};
    .MuiSvgIcon-root {
      font-size: 1rem;
    }
    &.Mui-checked .MuiSvgIcon-root {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  `,
  FieldContent: styled.div.attrs(({ field, viewportWidth }) => ({
    style: {
      width: `${(field.size.width / 100) * viewportWidth}px`,
    },
  }))`
    display: block;
    overflow: hidden;
    width: 90px;
    height: 16px;
    padding: 0 4px;
    color: ${({ theme }) => theme.palette.primary.light};
    background-color: #e5e7f0;
    font-size: 0.5rem;
    font-weight: 600;
    line-height: 1.5;
    text-overflow: ellipsis;
    white-space: nowrap;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.24);
    ${({ error, theme }) => error && css`
      color: ${theme.palette.status.error.main};
      background-color: #fae4e6;
    `}
  `,
  Input: styled(Input)`
    margin-bottom: 0.5rem;
    &.MuiInput-underline {
      ::after {
        border-bottom: 2px solid #0c8d6d;
        transform: scaleX(1);
      }
    }
  `,
  Length: styled.p`
    color: rgba(0, 0, 0, 0.54);
    font-size: ${({ theme }) => theme.typography.pxToRem(12)};
    line-height: 1.33;
    text-align: right;
  `,
};

const MultipleSelectionField = ({
  field, form, large, question, viewportWidth,
}) => {
  const [value, setValue] = useState('');
  const [prevValue, setPrevValue] = useState('');
  const [open, openDialog, closeDialog] = useModal();

  const error = useMemo(() => !!(
    get(field.name, form.errors) && get(field.name, form.touched)
  ), [field.name, form.errors, form.touched]);

  const options = useMemo(() => map((option) => ({
    ...option,
    value: option.type === 'TEXT' ? `other:${value}` : option.number,
  }), question.options), [question.options, value]);

  const handleCheckboxChange = (event) => {
    form.setFieldTouched(field.name, true);
    form.setFieldValue(
      field.name,
      includes(event.target.value, field.value)
        ? reject((val) => val === event.target.value, field.value)
        : [...(isArray(field.value) ? field.value : []), event.target.value],
    );
  };
  const handleInputChange = (event) => {
    if (size(event.target.value) <= MAX_LENGTH) {
      setValue(event.target.value);

      form.setFieldValue(field.name, [
        ...reject(includes('other:'), field.value),
        `other:${event.target.value}`,
      ]);
    }
  };
  const handleClick = () => {
    if (!some(includes('other:'), field.value)) {
      openDialog();
    }

    setPrevValue(value);
  };
  const handleCancel = () => {
    closeDialog();

    setValue(prevValue);

    form.setFieldValue(field.name, [...reject((val) => val === `other:${value}`, field.value), `other:${prevValue}`]);
  };
  const handleClose = () => closeDialog();

  useEffect(() => {
    if (some(includes('other:'), field.value) && value !== last(split('other:', find(includes('other:'), field.value)))) {
      setValue(last(split('other:', find(includes('other:'), field.value))));
    }
  }, [field.value, value]);

  return (
    <div>
      <Question error={error && !includes('other:', field.value)} large={large} question={question} />
      <ol>
        {map((option) => (
          // XXX: 하위 호환성 유지를 위한 필드. 추후 제거 예정
          <S.OptionItem
            key={option.number}
            position={option.position || { x: option.offsetX, y: option.offsetY }}
          >
            <S.Label htmlFor={`${question.number}-${option.number}`}>
              <S.OptionNumber
                error={option.value === 'other:'
                  ? error
                  : error && !includes('other:', field.value)}
                large={large}
              >
                {`${question.number}-${option.number}`}
              </S.OptionNumber>
              <S.Mesh>
                <S.Checkbox
                  id={`${question.number}-${option.number}`}
                  value={option.value}
                  checked={includes(toString(option.value), field.value)}
                  onChange={handleCheckboxChange}
                  onClick={option.type === 'TEXT' ? handleClick : undefined}
                />
              </S.Mesh>
              {option.type === 'TEXT' && (
                <S.FieldContent
                  name={field.name}
                  field={{ size: { width: getOr(20, 'width', option.size) } }}
                  viewportWidth={viewportWidth}
                  error={error}
                >
                  {value || '기타 의견을 입력해주세요.'}
                </S.FieldContent>
              )}
            </S.Label>
          </S.OptionItem>
        ), options)}
      </ol>
      <OtherResponseDialog
        open={open}
        onCancel={handleCancel}
        onClose={handleClose}
        disabled={!value || size(value) > MAX_LENGTH}
      >
        <S.Input
          type="text"
          value={value}
          onChange={handleInputChange}
          rowsMax={6}
          autoFocus
          fullWidth
          multiline
        />
        <S.Length>
          <data value={size(value)}>{size(value)}</data>
          /200
        </S.Length>
      </OtherResponseDialog>
    </div>
  );
};

MultipleSelectionField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({}).isRequired,
    touched: PropTypes.shape({}).isRequired,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
  }).isRequired,
  large: PropTypes.bool,
  question: PropTypes.shape({
    number: PropTypes.number,
    options: PropTypes.arrayOf(PropTypes.shape({})),
    type: PropTypes.oneOf(['SINGLE_SELECTION', 'MULTIPLE_SELECTION', 'DESCRIPTIVE']),
  }).isRequired,
  viewportWidth: PropTypes.number.isRequired,
};

MultipleSelectionField.defaultProps = {
  large: false,
};

export default MultipleSelectionField;
